/**
 * Validation for host/add-on supplied identity, decision, and exposure data.
 * Raw names, email addresses, phone numbers, and arbitrary traits are not part
 * of these contracts. Add-ons must normalize identifiers before calling PXL.
 */

export type IdentityMatchMethod =
  | "deterministic"
  | "probabilistic"
  | "declared";

export type IdentityResolutionInput = {
  provider: string;
  method: IdentityMatchMethod;
  confidence?: number;
  identifiers?: Array<{
    type: "email" | "phone" | "cookie" | "device" | "external" | "company";
    algorithm: "sha256" | "sha1" | "md5" | "provider";
    hash: string;
  }>;
};

export type DecisionInput = {
  decisionId: string;
  action: string;
  eligible: boolean;
  score?: number;
  reasonCodes?: string[];
  modelVersion?: string;
  policyVersion?: string;
  provider?: string;
};

export type ExposureInput = {
  decisionId: string;
  action: string;
  surface: string;
  experimentId?: string;
  variantId?: string;
};

export type OutcomeInput = {
  decisionId: string;
  outcome: string;
  success?: boolean;
  value?: number;
  currency?: string;
  provider?: string;
};

const TOKEN_PATTERN = /^[a-z0-9][a-z0-9._:-]*$/i;
const PROVIDER_PATTERN = /^[a-z0-9][a-z0-9_-]*$/i;
const PROVIDER_HASH_PATTERN = /^[a-z0-9+/_=.-]{8,256}$/i;

function normalizeToken(value: unknown, pattern: RegExp, maxLength: number) {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim();
  if (!normalized || normalized.length > maxLength) return undefined;
  if (!pattern.test(normalized)) return undefined;
  return normalized;
}

export function normalizeIdentityResolution(input: unknown) {
  if (!input || typeof input !== "object" || Array.isArray(input)) return null;
  const value = input as Record<string, unknown>;
  const provider = normalizeToken(value.provider, PROVIDER_PATTERN, 64);
  const method = value.method;
  if (
    !provider ||
    (method !== "deterministic" &&
      method !== "probabilistic" &&
      method !== "declared")
  ) {
    return null;
  }

  const allowedTypes = new Set([
    "email",
    "phone",
    "cookie",
    "device",
    "external",
    "company",
  ]);
  const allowedAlgorithms = new Set(["sha256", "sha1", "md5", "provider"]);
  const identifiers: Array<{
    type: string;
    algorithm: string;
    hash: string;
  }> = [];
  const seen = new Set<string>();

  if (Array.isArray(value.identifiers)) {
    for (const raw of value.identifiers.slice(0, 8)) {
      if (!raw || typeof raw !== "object" || Array.isArray(raw)) continue;
      const identifier = raw as Record<string, unknown>;
      const type = identifier.type;
      const algorithm = identifier.algorithm;
      const hash =
        algorithm === "md5"
          ? normalizeToken(identifier.hash, /^[a-f0-9]{32}$/i, 32)
          : algorithm === "sha1"
            ? normalizeToken(identifier.hash, /^[a-f0-9]{40}$/i, 40)
            : algorithm === "sha256"
              ? normalizeToken(identifier.hash, /^[a-f0-9]{64}$/i, 64)
              : algorithm === "provider"
                ? normalizeToken(identifier.hash, PROVIDER_HASH_PATTERN, 256)
                : undefined;
      if (
        typeof type !== "string" ||
        typeof algorithm !== "string" ||
        !allowedTypes.has(type) ||
        !allowedAlgorithms.has(algorithm) ||
        !hash
      ) {
        continue;
      }
      const key = `${type}:${algorithm}:${hash}`;
      if (seen.has(key)) continue;
      seen.add(key);
      identifiers.push({ type, algorithm, hash });
    }
  }
  if (identifiers.length === 0) return null;

  const confidence =
    typeof value.confidence === "number" && Number.isFinite(value.confidence)
      ? Math.min(Math.max(value.confidence, 0), 1)
      : undefined;
  const confidenceTier =
    method === "deterministic"
      ? "verified"
      : confidence !== undefined && confidence >= 0.8
        ? "high"
        : confidence !== undefined && confidence >= 0.5
          ? "medium"
          : "low";

  return {
    provider,
    method,
    confidence,
    confidence_tier: confidenceTier,
    identifier_types: identifiers.map(
      (identifier) => `${identifier.type}:${identifier.algorithm}`,
    ),
    identifiers,
  };
}

export function normalizeDecision(input: unknown) {
  if (!input || typeof input !== "object" || Array.isArray(input)) return null;
  const value = input as Record<string, unknown>;
  const decisionId = normalizeToken(value.decisionId, TOKEN_PATTERN, 120);
  const action = normalizeToken(value.action, TOKEN_PATTERN, 80);
  if (!decisionId || !action || typeof value.eligible !== "boolean") {
    return null;
  }

  const score =
    typeof value.score === "number" && Number.isFinite(value.score)
      ? Math.min(Math.max(value.score, 0), 100)
      : undefined;
  const reasonCodes = Array.isArray(value.reasonCodes)
    ? Array.from(
        new Set(
          value.reasonCodes
            .slice(0, 10)
            .map((reason) => normalizeToken(reason, TOKEN_PATTERN, 80))
            .filter((reason): reason is string => Boolean(reason)),
        ),
      )
    : [];

  return {
    decision_id: decisionId,
    action,
    eligible: value.eligible,
    score,
    reason_codes: reasonCodes,
    model_version: normalizeToken(value.modelVersion, TOKEN_PATTERN, 80),
    policy_version: normalizeToken(value.policyVersion, TOKEN_PATTERN, 80),
    provider: normalizeToken(value.provider, PROVIDER_PATTERN, 64),
  };
}

export function normalizeExposure(input: unknown) {
  if (!input || typeof input !== "object" || Array.isArray(input)) return null;
  const value = input as Record<string, unknown>;
  const decisionId = normalizeToken(value.decisionId, TOKEN_PATTERN, 120);
  const action = normalizeToken(value.action, TOKEN_PATTERN, 80);
  const surface = normalizeToken(value.surface, TOKEN_PATTERN, 80);
  if (!decisionId || !action || !surface) return null;

  return {
    decision_id: decisionId,
    action,
    surface,
    experiment_id: normalizeToken(value.experimentId, TOKEN_PATTERN, 120),
    variant_id: normalizeToken(value.variantId, TOKEN_PATTERN, 120),
  };
}

export function normalizeOutcome(input: unknown) {
  if (!input || typeof input !== "object" || Array.isArray(input)) return null;
  const value = input as Record<string, unknown>;
  const decisionId = normalizeToken(value.decisionId, TOKEN_PATTERN, 120);
  const outcome = normalizeToken(value.outcome, TOKEN_PATTERN, 80);
  if (!decisionId || !outcome) return null;

  const amount =
    typeof value.value === "number" && Number.isFinite(value.value)
      ? Math.min(Math.max(value.value, 0), 1_000_000_000)
      : undefined;
  const currency =
    typeof value.currency === "string" &&
    /^[a-z]{3}$/i.test(value.currency.trim())
      ? value.currency.trim().toUpperCase()
      : undefined;

  return {
    decision_id: decisionId,
    outcome,
    success: typeof value.success === "boolean" ? value.success : undefined,
    value: amount,
    currency,
    provider: normalizeToken(value.provider, PROVIDER_PATTERN, 64),
  };
}
